import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Table, Spinner, Card, Row, Col, Form, Badge } from "react-bootstrap";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation } from "react-router-dom";
import axios from "axios";

// Axios instance pointing to base API
const api = axios.create({ baseURL: API_WEB_URLS.BASE });

// Auth — same pattern as rest of app
const AUTH_USER_ID = 0;
const AUTH_TOKEN   = "token";

// Date Helpers for YTD (Year to Date)
const getYTDStartDate = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-indexed (0=Jan, 3=Apr)
  // Indian Financial Year starts on April 1st
  const fyYear = currentMonth < 3 ? currentYear - 1 : currentYear;
  return `${fyYear}-04-01`;
};

const getTodayDate = () => {
  return new Date().toISOString().split("T")[0];
};

const getCYStartDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-01-01`;
};

const getMonthStartDate = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};

// Helper to safely extract list from API response regardless of casing
const extractDataList = (res) => {
  if (!res) return [];
  const raw = res.data;
  if (Array.isArray(raw)) return raw.map(normalizeRow);
  if (raw?.data?.dataList && Array.isArray(raw.data.dataList)) return raw.data.dataList.map(normalizeRow);
  if (raw?.Data?.DataList && Array.isArray(raw.Data.DataList)) return raw.Data.DataList.map(normalizeRow);
  if (raw?.dataList && Array.isArray(raw.dataList)) return raw.dataList.map(normalizeRow);
  if (raw?.DataList && Array.isArray(raw.DataList)) return raw.DataList.map(normalizeRow);
  if (raw?.data && Array.isArray(raw.data)) return raw.data.map(normalizeRow);
  if (raw?.Data && Array.isArray(raw.Data)) return raw.Data.map(normalizeRow);
  return [];
};

// Normalizes row keys so both PascalCase and camelCase properties work everywhere
const normalizeRow = (row) => {
  if (!row || typeof row !== "object") return row;
  const n = { ...row };
  for (const [k, v] of Object.entries(row)) {
    const lower = k.charAt(0).toLowerCase() + k.slice(1);
    const upper = k.charAt(0).toUpperCase() + k.slice(1);
    if (!(lower in n)) n[lower] = v;
    if (!(upper in n)) n[upper] = v;
  }
  return n;
};

const LedgerReport = () => {
  const location = useLocation();

  // ─── State ───────────────────────────────────────────────────────────────
  const [allLedgersSummary, setAllLedgersSummary] = useState([]);  // All-ledger list with live balances
  const [selectedLedgerId,  setSelectedLedgerId]  = useState("");  // "" = show all-ledgers summary
  const [selectedCategory,  setSelectedCategory]  = useState("ALL");
  const [ledgerSearch,      setLedgerSearch]       = useState("");
  const [fromDate,          setFromDate]           = useState(getYTDStartDate());
  const [toDate,            setToDate]             = useState(getTodayDate());
  const [reportData,        setReportData]         = useState([]);  // Voucher lines for selected ledger
  const [openingRow,        setOpeningRow]         = useState(null); // Opening balance from SP result set 1
  const [loadingLedgers,    setLoadingLedgers]     = useState(false);
  const [loadingReport,     setLoadingReport]      = useState(false);
  const [error,             setError]              = useState("");

  // ─── Read ?ledgerId= from URL ─────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qLedgerId = params.get("ledgerId");
    if (qLedgerId) setSelectedLedgerId(qLedgerId);
  }, [location.search]);

  // ─── Fetch All-Ledgers Summary (dedicated SP: GetLedgerList_Report) ───────
  const loadAllLedgers = useCallback(async (fd, td) => {
    setLoadingLedgers(true);
    setError("");
    try {
      const params = {};
      if (fd) params.fromDate = fd;
      if (td) params.toDate   = td;

      const res = await api.get(
        `${API_WEB_URLS.LEDGER_REPORT_LIST}/${AUTH_USER_ID}/${AUTH_TOKEN}/List`,
        { params }
      );

      const rows = extractDataList(res);
      setAllLedgersSummary(rows);
    } catch (err) {
      console.error("Error loading all ledgers:", err);
      setError("Failed to load ledger list. Please check API connection.");
    } finally {
      setLoadingLedgers(false);
    }
  }, []);

  // ─── Fetch Single Ledger Statement (dedicated SP: GetLedgerStatement_Report)
  const loadReport = useCallback(async (ledgerId, fd, td) => {
    if (!ledgerId) return;
    setLoadingReport(true);
    setReportData([]);
    setOpeningRow(null);
    setError("");
    try {
      const params = {};
      if (fd) params.fromDate = fd;
      if (td) params.toDate   = td;

      const res = await api.get(
        `${API_WEB_URLS.LEDGER_REPORT_STATEMENT}/${AUTH_USER_ID}/${AUTH_TOKEN}/Statement/${ledgerId}`,
        { params }
      );

      const rows = extractDataList(res);
      if (rows.length > 0) {
        if (rows[0] && rows[0].OpeningBalance !== undefined && !rows[0].VoucherId) {
          setOpeningRow(rows[0]);
          setReportData(rows.slice(1));
        } else {
          setReportData(rows);
        }
      } else {
        setReportData([]);
      }
    } catch (err) {
      console.error("Error loading ledger statement:", err);
      setError("Failed to load ledger statement.");
    } finally {
      setLoadingReport(false);
    }
  }, []);

  // ─── Auto-fetch on mount AND whenever selectedLedgerId / fromDate / toDate changes ───
  useEffect(() => {
    if (selectedLedgerId && selectedLedgerId !== "ALL") {
      // Single ledger statement view
      loadReport(selectedLedgerId, fromDate, toDate);
    } else {
      // All ledgers summary view
      loadAllLedgers(fromDate, toDate);
    }
  }, [selectedLedgerId, fromDate, toDate]); // eslint-disable-line

  // Filtered ledgers list for dropdown & summary table
  const filteredLedgers = useMemo(() => {
    return allLedgersSummary.filter((l) => {
      const name = (l.LedgerName || "").toLowerCase();
      const code = (l.LedgerCode || "").toLowerCase();
      const group = (l.LedgerGroupName || "").toLowerCase();
      const search = ledgerSearch.toLowerCase();
      const matchSearch = !ledgerSearch || name.includes(search) || code.includes(search) || group.includes(search);

      if (!matchSearch) return false;

      if (selectedCategory === "DRIVER") {
        return l.F_LedgerGroupMaster === 10 || name.includes("driver") || name.includes("wallet");
      }
      if (selectedCategory === "COMPANY") {
        return l.Id === 31 || l.Id === 32 || l.Id === 33 || l.Id === 34 || l.Id === 35 || l.LedgerId === 31 || l.LedgerId === 32 || l.LedgerId === 33 || l.LedgerId === 34 || l.LedgerId === 35 || name.includes("company");
      }
      if (selectedCategory === "CASH") {
        return l.Id === 31 || l.LedgerId === 31 || name.includes("cash") || group.includes("cash");
      }
      if (selectedCategory === "ONLINE") {
        return l.Id === 32 || l.LedgerId === 32 || name.includes("bank") || name.includes("payu") || name.includes("upi") || name.includes("online") || group.includes("bank");
      }
      if (selectedCategory === "BANK") {
        return l.Id === 32 || l.LedgerId === 32 || name.includes("bank") || name.includes("cash") || name.includes("payu");
      }
      return true;
    });
  }, [allLedgersSummary, selectedCategory, ledgerSearch]);

  // Overall totals across all ledgers with dedicated Cash vs Online bifurcation
  const allLedgersTotals = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    let totalClosing = 0;

    let cashDebit = 0;
    let cashCredit = 0;
    let cashBalance = 0;

    let onlineDebit = 0;
    let onlineCredit = 0;
    let onlineBalance = 0;

    allLedgersSummary.forEach((l) => {
      const name = (l.LedgerName || "").toLowerCase();
      const group = (l.LedgerGroupName || "").toLowerCase();
      const idVal = Number(l.Id || l.LedgerId || 0);

      const isCash = idVal === 31 || name.includes("cash") || group.includes("cash");
      const isOnline = idVal === 32 || name.includes("bank") || name.includes("payu") || name.includes("upi") || name.includes("online") || group.includes("bank");

      if (isCash) {
        cashDebit += Number(l.TotalDebit || 0);
        cashCredit += Number(l.TotalCredit || 0);
        cashBalance += Number(l.ClosingBalance || l.OpeningBalance || 0);
      }
      if (isOnline) {
        onlineDebit += Number(l.TotalDebit || 0);
        onlineCredit += Number(l.TotalCredit || 0);
        onlineBalance += Number(l.ClosingBalance || l.OpeningBalance || 0);
      }
    });

    filteredLedgers.forEach((l) => {
      totalDebit += Number(l.TotalDebit || 0);
      totalCredit += Number(l.TotalCredit || 0);
      totalClosing += Number(l.ClosingBalance || l.OpeningBalance || 0);
    });

    return {
      count: filteredLedgers.length,
      totalDebit,
      totalCredit,
      totalClosing,
      cashDebit,
      cashCredit,
      cashBalance,
      onlineDebit,
      onlineCredit,
      onlineBalance,
    };
  }, [allLedgersSummary, filteredLedgers]);

  // Find selected ledger info (from allLedgersSummary — single source of truth)
  const selectedLedgerInfo = useMemo(() => {
    if (!selectedLedgerId || selectedLedgerId === "ALL") return null;
    return allLedgersSummary.find((l) => (l.LedgerId || l.Id)?.toString() === selectedLedgerId.toString()) || null;
  }, [allLedgersSummary, selectedLedgerId]);

  // Compute period opening balance and transactions with running balance for selected ledger
  const { processedTransactions, periodOpeningBalance, periodOpeningType, totals } = useMemo(() => {
    if (!selectedLedgerInfo) {
      return {
        processedTransactions: [],
        periodOpeningBalance: 0,
        periodOpeningType: "Dr",
        totals: { totalDebit: 0, totalCredit: 0, closingBalance: 0, closingType: "Dr" },
      };
    }

    const defaultType = selectedLedgerInfo.OpeningBalanceType || "Dr";
    const isDebitLedger = defaultType === "Dr";
    const initialOpBal = Number(selectedLedgerInfo.OpeningBalance || 0);

    // 1. Calculate opening balance accumulated up to fromDate
    let openingVal = isDebitLedger ? initialOpBal : initialOpBal;
    
    // Sort all vouchers chronologically
    const sorted = [...reportData].sort((a, b) => {
      const dateA = a.VoucherDate || "";
      const dateB = b.VoucherDate || "";
      return dateA.localeCompare(dateB) || (a.VoucherId || 0) - (b.VoucherId || 0);
    });

    const prePeriodTx = [];
    const inPeriodTx = [];

    sorted.forEach((tx) => {
      const txDate = tx.VoucherDate ? tx.VoucherDate.substring(0, 10) : "";
      if (fromDate && txDate && txDate < fromDate) {
        prePeriodTx.push(tx);
      } else if (!toDate || !txDate || txDate <= toDate) {
        inPeriodTx.push(tx);
      }
    });

    // Add pre-period transactions to initial opening balance
    prePeriodTx.forEach((tx) => {
      const amt = Number(tx.Amount || 0);
      if (isDebitLedger) {
        openingVal = tx.EntryType === "Dr" ? openingVal + amt : openingVal - amt;
      } else {
        openingVal = tx.EntryType === "Cr" ? openingVal + amt : openingVal - amt;
      }
    });

    const periodOpType = openingVal >= 0 ? defaultType : (defaultType === "Dr" ? "Cr" : "Dr");
    const periodOpAbs = Math.abs(openingVal);

    // 2. Process in-period transactions with running balance
    let currentBalance = openingVal;
    let debitSum = 0;
    let creditSum = 0;

    const processed = inPeriodTx.map((tx) => {
      const amt = Number(tx.Amount || 0);
      if (tx.EntryType === "Dr") debitSum += amt;
      else creditSum += amt;

      if (isDebitLedger) {
        currentBalance = tx.EntryType === "Dr" ? currentBalance + amt : currentBalance - amt;
      } else {
        currentBalance = tx.EntryType === "Cr" ? currentBalance + amt : currentBalance - amt;
      }

      return {
        ...tx,
        balance: Math.abs(currentBalance),
        balanceType: currentBalance >= 0 ? defaultType : (defaultType === "Dr" ? "Cr" : "Dr"),
      };
    });

    const closingType = currentBalance >= 0 ? defaultType : (defaultType === "Dr" ? "Cr" : "Dr");

    return {
      processedTransactions: processed,
      periodOpeningBalance: periodOpAbs,
      periodOpeningType: periodOpType,
      totals: {
        totalDebit: debitSum,
        totalCredit: creditSum,
        closingBalance: Math.abs(currentBalance),
        closingType: closingType,
      },
    };
  }, [reportData, selectedLedgerInfo, fromDate, toDate]);

  // Export to Excel (CSV)
  const handleExportExcel = () => {
    let csvContent = "";
    if (selectedLedgerInfo && processedTransactions.length > 0) {
      csvContent += `"Ledger Statement","${selectedLedgerInfo.LedgerName} (${selectedLedgerInfo.LedgerCode || `ID:${selectedLedgerInfo.Id}`})"\n`;
      csvContent += `"Period","${fromDate || "Start"} to ${toDate || "End"}"\n\n`;

      const headers = ["Date", "Voucher No", "Voucher Type", "Particulars", "Narration", "Debit (Dr)", "Credit (Cr)", "Balance"];
      csvContent += headers.map((h) => `"${h}"`).join(",") + "\n";

      const opDr = periodOpeningType === "Dr" ? periodOpeningBalance.toFixed(2) : "";
      const opCr = periodOpeningType === "Cr" ? periodOpeningBalance.toFixed(2) : "";
      csvContent += `"${fromDate || "-"}","-","-","Opening Balance","Brought Forward","${opDr}","${opCr}","${periodOpeningBalance.toFixed(2)} ${periodOpeningType}"\n`;

      processedTransactions.forEach((tx) => {
        const dr = tx.EntryType === "Dr" ? tx.Amount.toFixed(2) : "";
        const cr = tx.EntryType === "Cr" ? tx.Amount.toFixed(2) : "";
        const row = [
          tx.VoucherDate || "-",
          tx.VoucherNo || "-",
          tx.VoucherTypeName || "-",
          tx.OppositeLedgerName || "-",
          tx.Narration || "-",
          dr,
          cr,
          `${tx.balance.toFixed(2)} ${tx.balanceType}`,
        ];
        csvContent += row.map((val) => `"${val.replace(/"/g, '""')}"`).join(",") + "\n";
      });

      csvContent += `"-","-","-","Total","-","${totals.totalDebit.toFixed(2)}","${totals.totalCredit.toFixed(2)}","${totals.closingBalance.toFixed(2)} ${totals.closingType}"\n`;
    } else {
      // Export All Ledgers Summary
      csvContent += `"All Ledgers Summary Report","Kirayago Logistics"\n`;
      csvContent += `"Generated On","${new Date().toLocaleString("en-IN")}"\n\n`;

      const headers = ["S.No.", "Account Code", "Ledger Name", "Group / Category", "Opening Balance", "Total Debit (Dr)", "Total Credit (Cr)", "Closing Balance"];
      csvContent += headers.map((h) => `"${h}"`).join(",") + "\n";

      filteredLedgers.forEach((l, idx) => {
        const row = [
          idx + 1,
          l.LedgerCode || `L${l.Id || l.LedgerId}`,
          l.LedgerName,
          l.LedgerGroupName || l.GroupCategory || "General",
          `₹${(l.OpeningBalance || 0).toFixed(2)} ${l.OpeningBalanceType || "Dr"}`,
          `₹${Number(l.TotalDebit || 0).toFixed(2)}`,
          `₹${Number(l.TotalCredit || 0).toFixed(2)}`,
          `₹${Number(l.ClosingBalance || l.OpeningBalance || 0).toFixed(2)} ${l.ClosingBalanceType || "Dr"}`,
        ];
        csvContent += row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",") + "\n";
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const fileName = selectedLedgerInfo
      ? `Ledger_${selectedLedgerInfo.LedgerCode || "Report"}_${new Date().toISOString().split("T")[0]}.csv`
      : `All_Ledgers_Summary_${new Date().toISOString().split("T")[0]}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print PDF
  const handlePrint = () => {
    window.print();
  };

  const isAllLedgersView = !selectedLedgerId || selectedLedgerId === "ALL";

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Header Title & Actions */}
        <Row className="mb-3 align-items-center">
          <Col md={6}>
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  backgroundColor: "#FFF7ED",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 12,
                }}
              >
                <i className="bx bx-file text-warning" style={{ fontSize: 24, color: "#E87B2D" }}></i>
              </div>
              <div>
                <h4 className="mb-0 font-size-18 text-dark font-weight-bold">
                  {isAllLedgersView ? "General Ledger & All Accounts Summary" : `Ledger: ${selectedLedgerInfo?.LedgerName || "Statement"}`}
                </h4>
                <p className="text-muted mb-0 font-size-12">
                  {isAllLedgersView
                    ? "Complete overview of all company, driver wallet, and bank ledger balances"
                    : `Account code: ${selectedLedgerInfo?.LedgerCode || `ID:${selectedLedgerInfo?.Id}`} | Detailed Voucher Statement`}
                </p>
              </div>
            </div>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            {!isAllLedgersView && (
              <Button
                variant="outline-primary"
                size="sm"
                className="me-2"
                onClick={() => setSelectedLedgerId("ALL")}
              >
                <i className="bx bx-arrow-back me-1"></i> Back to All Ledgers
              </Button>
            )}
            <Button variant="outline-secondary" size="sm" className="me-2" onClick={handlePrint}>
              <i className="bx bx-printer me-1"></i> Print
            </Button>
            <Button variant="outline-success" size="sm" className="me-2" onClick={handleExportExcel}>
              <i className="bx bx-download me-1"></i> Export Excel
            </Button>
          </Col>
        </Row>

        {/* Filters Card */}
        <Card className="mb-4 shadow-sm border-0">
          <Card.Body>
            <Row className="align-items-end g-2">
              {/* Account Category Filter Tabs */}
              <Col md={12} className="mb-2">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <span className="font-size-11 fw-bold text-muted text-uppercase me-2">Account Type:</span>
                  {[
                    { id: "ALL", label: "All Ledgers" },
                    { id: "DRIVER", label: "Driver Wallets" },
                    { id: "CASH", label: "💵 Cash Accounts" },
                    { id: "ONLINE", label: "💳 Online / Bank / UPI" },
                    { id: "COMPANY", label: "Company Accounts" },
                  ].map((cat) => (
                    <Button
                      key={cat.id}
                      size="sm"
                      variant={selectedCategory === cat.id ? "primary" : "outline-secondary"}
                      onClick={() => setSelectedCategory(cat.id)}
                      style={{ fontSize: 11, padding: "3px 12px" }}
                    >
                      {cat.label}
                    </Button>
                  ))}
                  <div className="ms-auto" style={{ width: 220 }}>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search accounts / groups..."
                      value={ledgerSearch}
                      onChange={(e) => setLedgerSearch(e.target.value)}
                      style={{ fontSize: 11 }}
                    />
                  </div>
                </div>
              </Col>

              {/* Ledger Dropdown Filter */}
              <Col md={4} sm={12}>
                <Form.Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
                  FILTER BY ACCOUNT ({filteredLedgers.length} Ledgers)
                </Form.Label>
                {loadingLedgers ? (
                  <div style={{ fontSize: 12 }}>
                    <Spinner animation="border" size="sm" className="me-2" /> Loading accounts...
                  </div>
                ) : (
                  <Form.Select
                    size="sm"
                    value={selectedLedgerId}
                    onChange={(e) => setSelectedLedgerId(e.target.value)}
                    style={{ fontSize: 12, fontWeight: selectedLedgerId === "ALL" || !selectedLedgerId ? 700 : 500 }}
                  >
                    <option value="ALL">-- 📊 All Ledgers Summary (All Accounts) --</option>
                    {filteredLedgers.map((l) => {
                      const idVal = l.Id || l.LedgerId;
                      return (
                        <option key={idVal} value={idVal}>
                          {l.LedgerName} ({l.LedgerCode || `ID:${idVal}`})
                        </option>
                      );
                    })}
                  </Form.Select>
                )}
              </Col>

              {/* From Date Filter */}
              <Col md={3} sm={6}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", margin: 0 }}>
                    FROM DATE
                  </Form.Label>
                  <button
                    type="button"
                    className="btn btn-link p-0 font-size-10 text-primary text-decoration-none"
                    onClick={() => setFromDate(getYTDStartDate())}
                  >
                    Set FY YTD
                  </button>
                </div>
                <Form.Control
                  type="date"
                  size="sm"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ fontSize: 12 }}
                />
              </Col>

              {/* To Date Filter */}
              <Col md={3} sm={6}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", margin: 0 }}>
                    TO DATE
                  </Form.Label>
                  <button
                    type="button"
                    className="btn btn-link p-0 font-size-10 text-primary text-decoration-none"
                    onClick={() => setToDate(getTodayDate())}
                  >
                    Set Today
                  </button>
                </div>
                <Form.Control
                  type="date"
                  size="sm"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ fontSize: 12 }}
                />
              </Col>


            </Row>

            {/* Quick Date Presets Row */}
            <div className="d-flex flex-wrap gap-2 align-items-center pt-2 border-top mt-2">
              <span className="font-size-10 text-muted fw-bold text-uppercase">Quick Date Presets:</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary py-0 px-2 font-size-11"
                onClick={() => {
                  setFromDate(getYTDStartDate());
                  setToDate(getTodayDate());
                }}
              >
                📅 Financial Year (YTD)
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2 font-size-11"
                onClick={() => {
                  setFromDate(getCYStartDate());
                  setToDate(getTodayDate());
                }}
              >
                📅 Calendar Year (YTD)
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2 font-size-11"
                onClick={() => {
                  setFromDate(getMonthStartDate());
                  setToDate(getTodayDate());
                }}
              >
                📅 This Month
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2 font-size-11"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
              >
                📅 All Time
              </button>
            </div>
          </Card.Body>
        </Card>

        {/* ------------------------------------------------------------- */}
        {/* VIEW 1: ALL LEDGERS SUMMARY REGISTER (DEFAULT ON PAGE OPEN) */}
        {/* ------------------------------------------------------------- */}
        {isAllLedgersView ? (
          <>
            {/* Cash vs Online Liquidity Bifurcation Row */}
            <Row className="mb-3 g-2">
              <Col md={6}>
                <Card className="border-0 shadow-sm rounded-3 bg-light border-start border-4 border-warning">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="badge bg-warning text-dark fw-bold mb-1">💵 CASH BALANCES & FLOW</span>
                        <h4 className="fw-bold mb-0 text-dark">
                          ₹{allLedgersTotals.cashBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </h4>
                        <div className="small text-muted mt-1">
                          Credits / Inflow: <span className="text-success fw-semibold">₹{allLedgersTotals.cashCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span> | 
                          Debits / Outflow: <span className="text-danger fw-semibold">₹{allLedgersTotals.cashDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                      <Button size="sm" variant="outline-warning" className="text-dark fw-semibold" onClick={() => setSelectedCategory("CASH")}>
                        View Cash
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border-0 shadow-sm rounded-3 bg-light border-start border-4 border-info">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="badge bg-info text-white fw-bold mb-1">💳 ONLINE / BANK / UPI GATEWAYS</span>
                        <h4 className="fw-bold mb-0 text-primary">
                          ₹{allLedgersTotals.onlineBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </h4>
                        <div className="small text-muted mt-1">
                          Credits / Inflow: <span className="text-success fw-semibold">₹{allLedgersTotals.onlineCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span> | 
                          Debits / Outflow: <span className="text-danger fw-semibold">₹{allLedgersTotals.onlineDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                      <Button size="sm" variant="outline-info" className="fw-semibold" onClick={() => setSelectedCategory("ONLINE")}>
                        View Online
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* All Ledgers Top Summary Cards */}
            <Row className="mb-3">
              <Col xl={3} md={6}>
                <Card className="mini-stats-wid shadow-sm border-0">
                  <Card.Body className="py-2">
                    <p className="text-muted fw-medium mb-1 font-size-11 text-uppercase">Total Accounts</p>
                    <h5 className="mb-0 fw-bold text-dark">{allLedgersTotals.count} Ledgers</h5>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={3} md={6}>
                <Card className="mini-stats-wid shadow-sm border-0">
                  <Card.Body className="py-2">
                    <p className="text-muted fw-medium mb-1 font-size-11 text-uppercase">Total Debits (Dr)</p>
                    <h5 className="mb-0 fw-bold text-danger">
                      ₹{allLedgersTotals.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </h5>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={3} md={6}>
                <Card className="mini-stats-wid shadow-sm border-0">
                  <Card.Body className="py-2">
                    <p className="text-muted fw-medium mb-1 font-size-11 text-uppercase">Total Credits (Cr)</p>
                    <h5 className="mb-0 fw-bold text-success">
                      ₹{allLedgersTotals.totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </h5>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={3} md={6}>
                <Card className="mini-stats-wid shadow-sm border-0">
                  <Card.Body className="py-2">
                    <p className="text-muted fw-medium mb-1 font-size-11 text-uppercase">Total Net Volume</p>
                    <h5 className="mb-0 fw-bold text-primary">
                      ₹{allLedgersTotals.totalClosing.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </h5>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* All Ledgers Summary Table */}
            <Card className="shadow-sm border-0">
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-dark font-size-11 text-uppercase">
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th style={{ width: 100 }}>Code</th>
                      <th>Account / Ledger Name</th>
                      <th>Account Group / Head</th>
                      <th className="text-end" style={{ width: 140 }}>Opening (₹)</th>
                      <th className="text-end" style={{ width: 140 }}>Debit (Dr)</th>
                      <th className="text-end" style={{ width: 140 }}>Credit (Cr)</th>
                      <th className="text-end" style={{ width: 150 }}>Closing Balance</th>
                      <th className="text-center" style={{ width: 120 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingLedgers ? (
                      <tr>
                        <td colSpan={9} className="text-center py-5">
                          <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                          Loading all accounts register...
                        </td>
                      </tr>
                    ) : filteredLedgers.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-4 text-muted">
                          No ledger accounts found
                        </td>
                      </tr>
                    ) : (
                      filteredLedgers.map((l, idx) => {
                        const idVal = l.Id || l.LedgerId;
                        const isDriver = l.F_LedgerGroupMaster === 10 || l.LedgerName?.toLowerCase().includes("driver");
                        const isBank = idVal === 32 || l.LedgerName?.toLowerCase().includes("bank");
                        const isIncome = idVal === 31 || idVal === 33 || l.LedgerName?.toLowerCase().includes("commission");

                        return (
                          <tr
                            key={idVal}
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedLedgerId(String(idVal))}
                          >
                            <td className="text-muted font-size-11">{idx + 1}</td>
                            <td>
                              <span className="badge bg-light text-dark font-size-10">{l.LedgerCode || `L${idVal}`}</span>
                            </td>
                            <td>
                              <span className="fw-bold text-primary font-size-13">{l.LedgerName}</span>
                            </td>
                            <td>
                              <Badge bg={isDriver ? "warning" : isBank ? "success" : isIncome ? "info" : "secondary"}>
                                {l.LedgerGroupName || (isDriver ? "Driver Wallet" : isBank ? "Bank / Cash" : isIncome ? "Revenue / Commission" : "General Account")}
                              </Badge>
                            </td>
                            <td className="text-end font-size-12">
                              ₹{Number(l.OpeningBalance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}{" "}
                              <small className="text-muted">{l.OpeningBalanceType || "Dr"}</small>
                            </td>
                            <td className="text-end font-size-12 text-danger fw-medium">
                              ₹{Number(l.TotalDebit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="text-end font-size-12 text-success fw-medium">
                              ₹{Number(l.TotalCredit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="text-end font-size-13 fw-bold text-dark">
                              ₹{Number(l.ClosingBalance || l.OpeningBalance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}{" "}
                              <small className="text-muted">{l.ClosingBalanceType || l.OpeningBalanceType || "Dr"}</small>
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                style={{ fontSize: 11, padding: "2px 8px" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLedgerId(String(idVal));
                                }}
                              >
                                View Statement
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot className="table-light font-size-12">
                    <tr className="border-top-2 fw-bold">
                      <td colSpan={4} className="text-end">TOTAL ({filteredLedgers.length} ACCOUNTS):</td>
                      <td className="text-end">-</td>
                      <td className="text-end text-danger">₹{allLedgersTotals.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="text-end text-success">₹{allLedgersTotals.totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="text-end text-primary">₹{allLedgersTotals.totalClosing.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </Card.Body>
            </Card>
          </>
        ) : (
          /* ------------------------------------------------------------- */
          /* VIEW 2: SINGLE LEDGER DETAILED VOUCHER STATEMENT               */
          /* ------------------------------------------------------------- */
          <>
            {/* Single Ledger Summary Cards */}
            <Row className="mb-4">
              <Col md={3} sm={6} className="mb-2">
                <Card style={{ borderLeft: "4px solid #007bff" }} className="shadow-sm border-0">
                  <Card.Body className="py-2">
                    <div style={{ fontSize: 11, color: "#6c757d", textTransform: "uppercase" }}>
                      {fromDate ? "Period Opening Balance" : "Opening Balance"}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      ₹{periodOpeningBalance.toFixed(2)} {periodOpeningType}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-2">
                <Card style={{ borderLeft: "4px solid #dc3545" }} className="shadow-sm border-0">
                  <Card.Body className="py-2">
                    <div style={{ fontSize: 11, color: "#6c757d", textTransform: "uppercase" }}>Total Debits (Dr)</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#dc3545" }}>
                      ₹{totals.totalDebit.toFixed(2)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-2">
                <Card style={{ borderLeft: "4px solid #28a745" }} className="shadow-sm border-0">
                  <Card.Body className="py-2">
                    <div style={{ fontSize: 11, color: "#6c757d", textTransform: "uppercase" }}>Total Credits (Cr)</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#28a745" }}>
                      ₹{totals.totalCredit.toFixed(2)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-2">
                <Card style={{ borderLeft: "4px solid #fd7e14" }} className="shadow-sm border-0">
                  <Card.Body className="py-2">
                    <div style={{ fontSize: 11, color: "#6c757d", textTransform: "uppercase" }}>Closing Balance</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#fd7e14" }}>
                      ₹{totals.closingBalance.toFixed(2)} {totals.closingType}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Single Ledger Transactions Table */}
            <Card className="shadow-sm border-0">
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle font-size-12">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: 110 }}>Date</th>
                      <th style={{ width: 120 }}>Voucher No</th>
                      <th style={{ width: 130 }}>Voucher Type</th>
                      <th>Particulars / Opposite Account</th>
                      <th>Narration</th>
                      <th className="text-end" style={{ width: 120 }}>Debit (Dr)</th>
                      <th className="text-end" style={{ width: 120 }}>Credit (Cr)</th>
                      <th className="text-end" style={{ width: 140 }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance Row */}
                    <tr style={{ backgroundColor: "#f8f9fa", fontWeight: 600 }}>
                      <td>{fromDate || "-"}</td>
                      <td>-</td>
                      <td>-</td>
                      <td>Opening Balance</td>
                      <td>{fromDate ? `Balance brought forward as on ${fromDate}` : "Initial Opening Balance"}</td>
                      <td className="text-end">
                        {periodOpeningType === "Dr" ? `₹${periodOpeningBalance.toFixed(2)}` : "-"}
                      </td>
                      <td className="text-end">
                        {periodOpeningType === "Cr" ? `₹${periodOpeningBalance.toFixed(2)}` : "-"}
                      </td>
                      <td className="text-end fw-bold">
                        ₹{periodOpeningBalance.toFixed(2)} {periodOpeningType}
                      </td>
                    </tr>

                    {/* Transaction Rows */}
                    {loadingReport ? (
                      <tr>
                        <td colSpan={8} className="text-center py-5">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading ledger vouchers...
                        </td>
                      </tr>
                    ) : processedTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4 text-muted">
                          No transactions found for this period
                        </td>
                      </tr>
                    ) : (
                      processedTransactions.map((tx, idx) => (
                        <tr key={idx}>
                          <td>{tx.VoucherDate || "-"}</td>
                          <td>
                            <span className="badge bg-light text-dark font-size-11">{tx.VoucherNo || "-"}</span>
                          </td>
                          <td>
                            <Badge bg={tx.VoucherTypeName === "Ride Complete" ? "info" : tx.VoucherTypeName === "Wallet Topup" ? "success" : "secondary"}>
                              {tx.VoucherTypeName || "Journal"}
                            </Badge>
                          </td>
                          <td className="fw-medium text-dark">{tx.OppositeLedgerName || "-"}</td>
                          <td className="text-muted font-size-11">{tx.Narration || "-"}</td>
                          <td className="text-end text-danger fw-medium">
                            {tx.EntryType === "Dr" ? `₹${Number(tx.Amount).toFixed(2)}` : "-"}
                          </td>
                          <td className="text-end text-success fw-medium">
                            {tx.EntryType === "Cr" ? `₹${Number(tx.Amount).toFixed(2)}` : "-"}
                          </td>
                          <td className="text-end fw-bold">
                            ₹{tx.balance.toFixed(2)} <small className="text-muted">{tx.balanceType}</small>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot className="table-light font-size-12">
                    <tr className="border-top-2 fw-bold">
                      <td colSpan={5} className="text-end">TOTAL FOR PERIOD:</td>
                      <td className="text-end text-danger">₹{totals.totalDebit.toFixed(2)}</td>
                      <td className="text-end text-success">₹{totals.totalCredit.toFixed(2)}</td>
                      <td className="text-end text-primary">₹{totals.closingBalance.toFixed(2)} {totals.closingType}</td>
                    </tr>
                  </tfoot>
                </Table>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default LedgerReport;
